import React, { useState } from 'react';
import { Controller, Control } from 'react-hook-form';
import SelectList, { components } from 'react-select';
import { useDispatch } from 'react-redux';
import { addIsbnPage, resetIsbnPage, setIsbnString } from '../redux/reducers/ISBNSReducer';
import { TitleDetailsFormValues } from '../components/Title Details/TitleDetails';
import { MdCheck, MdContentCopy } from 'react-icons/md';

interface OptionType {
  value: string;
  label: string;
}

interface SelectInputProps {
  control: Control<any>;
  name: string;
  options: OptionType[];
  placeholder: string;
  onChangeCallback?: (value: any) => void;
  watch: (name: string) => any;
  isFirefox?: boolean;
  hasMoreIsbn?: boolean;
  copyBtn?: boolean;
  disableClear?: boolean;
}

const SelectInput: React.FC<SelectInputProps> = ({
  control,
  name,
  options,
  placeholder,
  onChangeCallback,
  watch,
  hasMoreIsbn = false,
  isFirefox = false,
  copyBtn = false,
  disableClear = false,
}) => {
  const dispatch = useDispatch();
  const [copied, setCopied] = useState(false);

  // Reorder the options to ensure the selected value is at the top
  const reorderOptions = (selectedValue: any, options: any) => {
    if (!selectedValue) return options;
    const selectedId = selectedValue.value;
    // Filter out the selected value and put it at the top
    const filteredOptions = options.filter(
      (option: OptionType) => selectedId !== option.value
    );
    return [selectedValue, ...filteredOptions];
  };

  // Get the reordered options based on the current selected value
  const reorderedOptions = reorderOptions(watch(name), options);

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        onChange: onChangeCallback,
      }}
      render={({ field: { onChange, onBlur, value, name, ref } }) => (
        <div className="relative flex flex-col md:w-full">
          <SelectList
            value={value}
            classNamePrefix="custom-select"
            placeholder={placeholder}
            backspaceRemovesValue={true}
            onMenuScrollToBottom={() => {
              if (hasMoreIsbn) {
                dispatch(addIsbnPage());
              }
            }}
            onInputChange={(inputValue) => {
              dispatch(resetIsbnPage());
              dispatch(setIsbnString(inputValue));
            }}
            ref={ref}
            id={`${name}-selector`}
            name={name}
            onBlur={onBlur}
            noOptionsMessage={() => 'No options found'}
            components={{
              MultiValueContainer: (props) => (
                <components.MultiValueContainer {...props}>
                  <div className="flex">{props.children}</div>
                </components.MultiValueContainer>
              ),
              ...(copyBtn && {
                DropdownIndicator: (props) => {
                  const handleCopy = async (e: any) => {
                        e.stopPropagation();
                        e.preventDefault();
                        
                        if (!value || value == null || value == undefined) return
                        try {
                          await navigator.clipboard.writeText(value?.label || "");
                          setCopied(true);

                          // Change back to copy icon after 2 seconds
                          setTimeout(() => {
                            setCopied(false);
                          }, 2000);
                        } catch (err) {
                          console.error("Failed to copy:", err);
                        }
                      };

                  return (
                    <components.DropdownIndicator 
                      {...props}
                      innerProps={{
                        ...props.innerProps,
                        onMouseDown: (e: any) => {
                          // Prevent the dropdown from opening when clicking the copy button area
                          e.preventDefault();
                          e.stopPropagation();
                        },
                        onClick: (e: any) => {
                          // Prevent the dropdown from opening when clicking the copy button area
                          e.preventDefault();
                          e.stopPropagation();
                        },
                      }}
                    >
                      <button 
                        onMouseDown={handleCopy}
                        onClick={handleCopy}
                        className="cursor-pointer"
                        type="button"
                      >
                        {copied ? (
                          <><MdCheck size={20} className="text-green-500" /></>
                        ) : (
                          <><MdContentCopy size={20} className="cursor-pointer" title='copy'/></>
                        )}
                      </button>

                    </components.DropdownIndicator>
                  );
                },
              }),
            }}
            className="border border-solid border-neutral-300 font-poppins rounded-lg w-full lg:text-[14px] sm:text-[10px]"
            styles={{
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected || state.isFocused ? '#E6E6E6' : '',
                color: state.isSelected ? 'black' : 'black',
              }),
              // input: (provided) => ({
              //   ...provided,
              //   position: watch(name) ? 'relative' : 'absolute',
              //   left: 6,
              //   marginRight: '60px',
              // }),
              // multiValue: (provided) => ({
              //   ...provided,
              //   height: '30px',
              //   display: 'flex',
              //   alignItems: 'center',
              //   borderRadius: '6px',
              //   minWidth: 'none',
              // }),
              menu: (provided) => ({
                ...provided,
                zIndex: 1050,
                position: 'absolute',
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
              }),
              menuPortal: (provided) => ({
                ...provided,
                zIndex: 1050,
              }),
              // valueContainer: (provided) => ({
              //   ...provided,
              //   position: 'relative',
              //   maxWidth: '28vw',
              //   width: '100%',
              //   paddingLeft: '8px',
              //   paddingRight: '8px',
              //   display: 'flex',
              //   alignItems: 'center',
              //   height: '40px',
              //   overflowX: 'auto',
              //   overflowY: 'hidden',
              //   minWidth: '200px',
              //   boxSizing: 'border-box',
              //   ...(isFirefox && {
              //     maxWidth: 'calc(28vw + 10px)',
              //     scrollbarWidth: 'thin',
              //   }),
              //   '@media (max-width:600px)': {
              //     maxWidth: '55vw',
              //   },
              // }),
              control: (provided) => ({
                display: 'flex',
                height: '40px',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }),
              menuList: (provided) => ({
                maxHeight: '200px',
                overflow: 'hidden',
                overflowY: 'auto',
              }),
              clearIndicator: (provided) => ({
                ...provided,
                cursor: 'pointer',
              }),
            }}
            isClearable={!disableClear && !!value?.value}
            onChange={(newValue: any) => {
              if (newValue && newValue.value !== value?.value) {
                onChange(newValue); // Only update if the option is not selected
              }
              if (newValue === null) onChange(newValue)
            }}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            options={reorderedOptions}
            hideSelectedOptions={false}
          />
        </div>
      )}
    />
  );
};

export default SelectInput;
